import {
  CheckCircle, FileText, Calendar, ArrowRight,
  ClipboardList, Search, Sparkles, ThumbsUp,
  Target, Users, Star, Heart, Brain, Shield, Zap,
  type LucideIcon,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { AccessibilityButton } from "@/components/AccessibilityButton";
import { openGoogleCalendar } from "@/lib/utils";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { usePageVisibility } from "@/hooks/usePageVisibility";
import { PageUnavailable } from "@/components/ui/PageUnavailable";

// ── Icon map for dynamic icon resolution ──────────────────────────────────────
const ICON_MAP: Record<string, LucideIcon> = {
  ClipboardList, Search, Sparkles, ThumbsUp,
  Target, Users, CheckCircle, FileText, Calendar, ArrowRight,
  Star, Heart, Brain, Shield, Zap,
};

// ── Default values ─────────────────────────────────────────────────────────────
const DEFAULT_HERO = {
  label: "100% Gratuita",
  labelIcon: "FileText",
  title: "Avaliação de Caso",
  subtitle: "Análise personalizada e gratuita do seu perfil nutricional para definir o melhor plano de tratamento.",
  badges: [
    { label: "Sem compromisso",     icon: "CheckCircle" },
    { label: "Resposta em 24h",     icon: "CheckCircle" },
    { label: "Plano personalizado", icon: "CheckCircle" },
  ],
};

const DEFAULT_OQUE = {
  greenTitle: "O que é a Avaliação de Caso?",
  greenDesc: "Um processo inicial para analisar seu histórico, hábitos e objetivos — sem nenhum custo.",
  greenButtonText: "Solicitar Avaliação",
  desc1: "A Avaliação de Caso é um processo inicial onde analisamos seu histórico de saúde, hábitos alimentares, estilo de vida e objetivos para criar uma estratégia nutricional personalizada e eficaz.",
  desc2: "Durante essa análise, identificamos as melhores abordagens para seu perfil, considerando suas necessidades específicas, preferências alimentares e rotina diária.",
  bullets: [
    "Análise completa do histórico alimentar",
    "Identificação de objetivos e necessidades",
    "Proposta de plano de tratamento personalizado",
    "Orientações iniciais sem compromisso",
  ],
};

const DEFAULT_STEPS = [
  { icon: "ClipboardList", num: "1", title: "Questionário",  desc: "Preencha nosso formulário detalhado sobre saúde, hábitos e objetivos." },
  { icon: "Search",        num: "2", title: "Análise",       desc: "Nossa equipe analisa suas informações de forma criteriosa." },
  { icon: "Sparkles",      num: "3", title: "Proposta",      desc: "Você recebe uma proposta personalizada de tratamento." },
  { icon: "ThumbsUp",      num: "4", title: "Decisão",       desc: "Você decide com tranquilidade se quer iniciar o acompanhamento." },
];

const DEFAULT_PROFILES = [
  { icon: "Target",      title: "Objetivos Específicos", desc: "Emagrecimento, ganho de massa muscular, melhora da saúde ou performance esportiva." },
  { icon: "Users",       title: "Famílias",              desc: "Famílias que querem estabelecer hábitos alimentares saudáveis para todos os membros." },
  { icon: "CheckCircle", title: "Condições de Saúde",    desc: "Diabetes, hipertensão, colesterol alto ou outras condições que exigem atenção nutricional." },
];

const DEFAULT_CTA = {
  title: "Pronto para começar?",
  desc: "Entre em contato pelo WhatsApp ou agende sua avaliação gratuita diretamente pela agenda online.",
  btn1Text: "Agendar Avaliação Gratuita",
  btn1Url: "",
  btn2Text: "Falar no WhatsApp",
  btn2Url: "https://wa.me/5511998874081",
};

// ── Helpers ────────────────────────────────────────────────────────────────────
function getYouTubeEmbedUrl(url: string): string | null {
  const patterns = [
    /youtu\.be\/([^?&]+)/,
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtube\.com\/embed\/([^?&]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
  }
  return null;
}

function isExternalOnlyVideo(url: string): boolean {
  return /instagram\.com|tiktok\.com|facebook\.com|fb\.watch/.test(url);
}

function getExternalPlatformLabel(url: string): string {
  if (/instagram\.com/.test(url)) return "Instagram";
  if (/tiktok\.com/.test(url)) return "TikTok";
  if (/facebook\.com|fb\.watch/.test(url)) return "Facebook";
  return "Ver vídeo";
}

function parseJson<T>(raw: string | undefined, fallback: T): T {
  if (!raw) return fallback;
  try { return { ...fallback, ...JSON.parse(raw) } as T; } catch { return fallback; }
}

function parseJsonArray<T>(raw: string | undefined, fallback: T[]): T[] {
  if (!raw) return fallback;
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) && p.length > 0 ? p : fallback;
  } catch { return fallback; }
}

export default function AvaliacaoCaso() {
  const { isVisible } = usePageVisibility();
  const { getSetting } = useSiteSettings();

  if (!isVisible("page_avaliacoes_visible")) {
    return <PageUnavailable />;
  }

  const hero     = parseJson(getSetting('avaliacoes_hero'),     DEFAULT_HERO);
  const oque     = parseJson(getSetting('avaliacoes_oque'),     DEFAULT_OQUE);
  const steps    = parseJsonArray(getSetting('avaliacoes_steps'),    DEFAULT_STEPS);
  const profiles = parseJsonArray(getSetting('avaliacoes_profiles'), DEFAULT_PROFILES);
  const profilesVisible = getSetting('avaliacoes_profiles_visible', 'true') !== 'false';
  const cta      = parseJson(getSetting('avaliacoes_cta'),     DEFAULT_CTA);
  const videoUrl = getSetting('avaliacoes_video', '');

  // Ensure arrays are properly typed after merge
  const heroBadges = (Array.isArray(hero.badges) ? hero.badges : DEFAULT_HERO.badges)
    .map((b: any) => typeof b === 'string' ? { label: b, icon: 'CheckCircle' } : b) as { label: string; icon: string }[];
  const oqueBullets: string[] = Array.isArray((oque as typeof DEFAULT_OQUE).bullets) ? (oque as typeof DEFAULT_OQUE).bullets : DEFAULT_OQUE.bullets;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="pt-24 bg-brand-primary py-14 sm:py-20">
        <div className="container mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 text-white/90 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wide uppercase">
            {(() => { const LabelIcon = ICON_MAP[(hero as any).labelIcon] || FileText; return <LabelIcon className="w-3.5 h-3.5" />; })()}
            {hero.label}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-primary mb-4 leading-tight">
            {hero.title}
          </h1>
          <p className="text-white/75 text-base sm:text-lg max-w-2xl mx-auto font-primary">
            {hero.subtitle}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            {heroBadges.map((b) => {
              const Icon = ICON_MAP[b.icon] || CheckCircle;
              return (
                <div key={b.label} className="flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-4 py-2 rounded-full">
                  <Icon className="w-4 h-4 text-white/70" />
                  {b.label}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── O que é ──────────────────────────────────────────── */}
      <section className="py-14">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="rounded-2xl overflow-hidden shadow-lg flex flex-col lg:flex-row">

            {/* painel verde */}
            <div className="bg-brand-primary lg:w-72 lg:shrink-0 flex flex-col justify-between p-8 gap-6">
              <div>
                <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center mb-5">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white font-primary leading-tight mb-3">
                  {(oque as typeof DEFAULT_OQUE).greenTitle}
                </h2>
                <p className="text-white/70 text-sm leading-relaxed">
                  {(oque as typeof DEFAULT_OQUE).greenDesc}
                </p>
              </div>
              <Button
                className="w-full bg-white text-brand-primary hover:bg-white/90 font-primary font-semibold shadow-md group"
                onClick={openGoogleCalendar}
              >
                <Calendar className="w-4 h-4 mr-2" />
                {(oque as typeof DEFAULT_OQUE).greenButtonText}
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </div>

            {/* conteúdo */}
            <div className="bg-white flex-1 p-7 sm:p-8 space-y-5">
              <p className="text-gray-600 text-sm leading-relaxed">
                {(oque as typeof DEFAULT_OQUE).desc1}
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                {(oque as typeof DEFAULT_OQUE).desc2}
              </p>
              <ul className="space-y-2.5">
                {oqueBullets.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-brand-primary shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* ── Como funciona ────────────────────────────────────── */}
      <section className="py-14 bg-white border-y border-gray-100">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-10">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Processo</p>
            <h2 className="text-2xl font-bold text-gray-800 font-primary">Como Funciona</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((step) => {
              const Icon = ICON_MAP[step.icon] || ClipboardList;
              return (
                <div key={step.num} className="relative bg-gray-50 rounded-2xl p-6 flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 bg-brand-primary/10 rounded-xl flex items-center justify-center">
                      <Icon className="w-5 h-5 text-brand-primary" />
                    </div>
                    <span className="text-3xl font-extrabold text-brand-primary/10 font-primary leading-none">{step.num}</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 font-primary">{step.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Para quem é ──────────────────────────────────────── */}
      {profilesVisible && <section className="py-14">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-10">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Indicado para</p>
            <h2 className="text-2xl font-bold text-gray-800 font-primary">Para Quem é a Avaliação?</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {profiles.map((profile) => {
              const Icon = ICON_MAP[profile.icon] || Star;
              return (
                <div key={profile.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-6 flex flex-col gap-3">
                  <div className="w-11 h-11 bg-brand-primary/8 rounded-xl flex items-center justify-center">
                    <Icon className="w-5 h-5 text-brand-primary" />
                  </div>
                  <h3 className="font-semibold text-gray-800 font-primary">{profile.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{profile.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>}

      {/* ── Vídeo convite ────────────────────────────────────── */}
      {videoUrl && (
        <section className="py-14 bg-white border-y border-gray-100">
          <div className="container mx-auto px-6 max-w-3xl">
            <div className="text-center mb-8">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Assista</p>
              <h2 className="text-2xl font-bold text-gray-800 font-primary">Veja o Convite da Dra. Brenda</h2>
            </div>
            {getYouTubeEmbedUrl(videoUrl) ? (
              <div className="rounded-2xl overflow-hidden shadow-lg aspect-video">
                <iframe
                  src={getYouTubeEmbedUrl(videoUrl)!}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : isExternalOnlyVideo(videoUrl) ? (
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-brand-primary to-brand-primary/80 rounded-2xl shadow-lg p-12 text-white hover:opacity-90 transition-opacity"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <div className="text-center">
                  <p className="font-bold text-lg font-primary">Assistir no {getExternalPlatformLabel(videoUrl)}</p>
                  <p className="text-white/70 text-sm mt-1">Clique para abrir o vídeo</p>
                </div>
                <div className="flex items-center gap-1.5 bg-white/15 px-4 py-2 rounded-full text-sm font-medium">
                  <ArrowRight className="w-4 h-4" />
                  Abrir vídeo
                </div>
              </a>
            ) : (
              <div className="rounded-2xl overflow-hidden shadow-lg aspect-video">
                <video src={videoUrl} controls className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── CTA final ────────────────────────────────────────── */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="bg-brand-primary rounded-2xl px-8 py-10 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
            <div className="relative z-10">
              <h2 className="text-xl sm:text-2xl font-bold text-white font-primary mb-3">
                {(cta as typeof DEFAULT_CTA).title}
              </h2>
              <p className="text-white/75 text-sm mb-7 max-w-md mx-auto">
                {(cta as typeof DEFAULT_CTA).desc}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  className="bg-white text-brand-primary hover:bg-white/90 font-primary font-semibold shadow-md"
                  onClick={(cta as typeof DEFAULT_CTA).btn1Url ? () => window.open((cta as typeof DEFAULT_CTA).btn1Url, '_blank') : openGoogleCalendar}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {(cta as typeof DEFAULT_CTA).btn1Text}
                </Button>
                <a
                  href={(cta as typeof DEFAULT_CTA).btn2Url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white hover:bg-white/10 font-primary font-semibold rounded-md px-4 py-2 transition-colors text-sm"
                >
                  <FaWhatsapp className="w-4 h-4" />
                  {(cta as typeof DEFAULT_CTA).btn2Text}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
      <AccessibilityButton />
    </div>
  );
}
